import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/* ──────────────────────────────────────────────────────────────────────────
 * Wishlist = purely client-side favourites, persisted in localStorage.
 * There is no wishlist table on the server, and the brief allows local storage
 * when backend support is absent — so this never calls the API.
 * ────────────────────────────────────────────────────────────────────────── */

const WishlistContext = createContext(null);
const STORAGE_KEY = 'moji_wishlist_v1';

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}

export function WishlistProvider({ children }) {
  const [ids, setIds] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
      /* ignore */
    }
  }, [ids]);

  const has = useCallback((id) => ids.includes(String(id)), [ids]);

  const toggle = useCallback((id) => {
    const key = String(id);
    setIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }, []);

  const value = useMemo(
    () => ({ ids, has, toggle, count: ids.length }),
    [ids, has, toggle]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
